import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useRef } from "react";
import { useTransfer2Card } from "@/hooks/use-mutations";
import { formatAmount } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useModalStore } from "@/zustand/modalStore";
import { useGetUserAssets } from "@/hooks/use-queries";

const tokenIconMap: Record<string, string> = {
  USDT: "/icons/usdt.svg",
  USDC: "/icons/usdc.svg",
};

const networkIconMap: Record<string, string> = {
  TRC20: "/icons/trc20.png",
  ERC20: "/icons/erc20.svg",
};

type Asset = {
  id: string;
  balance: string | number;
  token: { id: number; type: string; symbol: string };
};

const FundCard = ({
  cardData,
  closeModal,
}: {
  closeModal: () => void;
  cardData: Card;
}) => {
  const queryClient = useQueryClient();
  const { data: userAssets } = useGetUserAssets();
  const totalBalance = (userAssets ?? []).reduce(
    (sum: number, a: Asset) => sum + Number(a.balance),
    0,
  );
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const { openModal } = useModalStore();
  const submittedAtRef = useRef<string>("");

  const selectedBalance = selectedAsset
    ? Number(selectedAsset.balance)
    : totalBalance;

  const { mutateAsync: transfer2Card, isPending: isTransferPending } =
    useTransfer2Card({
      id: cardData.id,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["deposits"] });
        queryClient.invalidateQueries({ queryKey: ["cardInfo", cardData.id] });
        openModal("success", {
          type: "card-topup",
          topupMeta: {
            createdAt: submittedAtRef.current,
            amount: Number(amount),
            currency: "USD",
          },
        });
      },
    });

  const onSubmit = () => {
    submittedAtRef.current = new Date().toISOString();
    transfer2Card({
      side: "fund",
      amount: amount,
      ...(selectedAsset ? { assetId: selectedAsset.token.id } : {}),
    });
  };

  return (
    <div className="relative z-[999]">
      <h4 className="mt-6 text-sm font-medium">Wallet balance</h4>
      <p className="text-2xl font-medium">${formatAmount(totalBalance)}</p>

      {(userAssets ?? []).length > 0 && (
        <>
          <h4 className="mt-4 text-sm font-medium">
            Select your sub wallet to fund your card
          </h4>
          <div className="mt-2 flex gap-2">
            {(userAssets as Asset[]).map((asset) => {
              const label = `${asset.token.type}-${asset.token.symbol}`;
              const icon =
                tokenIconMap[asset.token.symbol] ?? "/icons/usdt.svg";
              const isSelected = selectedAsset?.id === asset.id;
              return (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => {
                    setSelectedAsset(asset);
                    setAmount("");
                    setError("");
                  }}
                  className={`flex flex-1 flex-col gap-1 rounded-xl border p-2.5 text-left transition-all ${
                    isSelected
                      ? "border-dark-primary-main bg-dark-primary-50"
                      : "border-dark-stroke-3 bg-dark-grey-50 opacity-60 hover:opacity-80"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <div className="relative shrink-0">
                      <img src={icon} alt={label} className="size-6" />
                      <img
                        src={networkIconMap[asset.token.type] ?? ""}
                        alt={asset.token.type}
                        className="absolute -right-1 -bottom-1 size-3.5 rounded-full"
                      />
                    </div>
                    <p className="text-[10px] text-dark-text-300">{label}</p>
                  </div>
                  <p className="text-sm font-medium text-dark-text-400">
                    {Number(asset.balance).toFixed(2)}
                  </p>
                </button>
              );
            })}
          </div>
        </>
      )}

      <h4 className="mt-4 text-sm font-medium">Amount</h4>
      <Input
        type="text"
        inputMode="numeric"
        value={amount}
        onChange={(e) => {
          const value = e.target.value.replace(/[^0-9.]/g, "");
          setAmount(value);
          if (Number(value) > selectedBalance) {
            setError("Amount exceeds your wallet balance");
          } else {
            setError("");
          }
        }}
        placeholder="Enter amount"
        className="bg-dark-input border-dark-grey-300 placeholder:text-dark-text-300 relative mt-2 h-11 lg:text-xs lg:placeholder:text-xs"
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}

      <div className="mt-4">
        <h4 className="text-sm font-semibold">Fund your Card</h4>
        <div className="mt-1 flex items-center gap-3 text-xs">
          <span>
            {cardData.firstName} {cardData.lastName}
          </span>
          <span>**** {cardData.last4}</span>
        </div>
      </div>

      <Button
        isLoading={isTransferPending}
        onClick={onSubmit}
        disabled={
          !selectedAsset ||
          Number(amount) <= 0 ||
          Number(amount) > selectedBalance ||
          isTransferPending
        }
        className="text-dark-text-400 bg-dark-primary-main hover:bg-dark-primary-100 mt-4 h-11 w-full rounded-md"
      >
        Continue
      </Button>
    </div>
  );
};

export default FundCard;
